import { normalizePositions, toEpochMs, bundleTimeRange } from '../core/track.js';

export const TRACK_COLORS = ['#00BCD4', '#A855F7', '#F59E0B', '#22C55E'];

function displayName(user, fallback) {
    return user?.displayName || user?.email?.split('@')[0] || fallback;
}

/**
 * Les polygones de zone ont été stockés en [lat, lng] ou en [lng, lat] selon
 * les versions de l'app : un point dont la seconde valeur dépasse 35 alors que
 * la première reste sous 30 est très probablement inversé (France : lat ≈ 42-51,
 * lng ≈ -5-10). Même heuristique que l'ancienne carte du dashboard.
 */
export function normalizeZonePolygon(polygon) {
    if (!Array.isArray(polygon) || polygon.length < 3) return null;
    const points = polygon.map(pair => {
        const a = Number(Array.isArray(pair) ? pair[0] : pair?.lat ?? pair?.latitude);
        const b = Number(Array.isArray(pair) ? pair[1] : pair?.lng ?? pair?.longitude);
        if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
        return Math.abs(a) < 30 && b > 35 ? [b, a] : [a, b];
    }).filter(Boolean);
    return points.length >= 3 ? points : null;
}

/** Texte lisible d'un message RDV : certains sont des propositions sérialisées en JSON. */
export function messageText(raw) {
    if (typeof raw !== 'string') return '';
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{')) return raw;
    try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
            if (typeof parsed.text === 'string') return parsed.text;
            if (typeof parsed.message === 'string') return parsed.message;
            return 'Proposition de modification du rendez-vous';
        }
    } catch (_) { /* texte brut */ }
    return raw;
}

const MEDIA_LABELS = { video: 'Vidéo', audio: 'Audio', photo: 'Photo d’arrivée', gallery: 'Photos' };

/**
 * Convertit le détail admin actuel d'un rendez-vous (GET /admin/rendez-vous/:id)
 * en ActivityBundle. Quand le RDV passera en v2 (enveloppes ESCROW), seule
 * cette fonction changera : le lecteur ne voit que le bundle.
 */
export function rendezVousAdminToBundle(rdv, { apiBaseUrl = '', mediaBase = null } = {}) {
    if (!rdv || !rdv.id) return null;
    const base = apiBaseUrl.replace(/\/+$/, '');
    // Médias servis au dashboard (admin) ou au lien police (/api/rendez-vous/:id/enquete).
    const mediaRoot = (mediaBase || `${base}/admin/rendez-vous/${rdv.id}`).replace(/\/+$/, '');
    const users = [
        { user: rdv.creator, id: rdv.creator?.id ?? rdv.creatorUserId, role: 'Organisateur' },
        { user: rdv.participant, id: rdv.participant?.id ?? rdv.participantUserId, role: 'Participant' },
    ].filter(entry => entry.id !== null && entry.id !== undefined);

    const assets = Array.isArray(rdv.mediaAssets) ? rdv.mediaAssets : [];
    // Début et fin réels de chaque enregistrement (registre) : plus fiables que l'heure de réception des fichiers.
    const recordingTimes = new Map((Array.isArray(rdv.recordings) ? rdv.recordings : []).map(r => [r.recordingId, r]));
    const locations = Array.isArray(rdv.locations) ? rdv.locations : [];

    const tracks = users.map((entry, index) => {
        const userId = entry.id;
        const sameUser = value => String(value) === String(userId);
        const userAssets = assets.filter(asset => sameUser(asset.uploadedBy));
        // Un média par enregistrement : une personne peut filmer, s'arrêter, enregistrer le son,
        // puis filmer de nouveau. Les fichiers reçus avant les enregistrements multiples
        // (sans identifiant) forment un seul enregistrement.
        const media = ['video', 'audio'].flatMap(type => {
            const byRecording = new Map();
            userAssets
                .filter(asset => asset.mediaType === type && asset.fileName !== 'playlist.m3u8' && !/\.m3u8$/i.test(asset.fileName || ''))
                .forEach(asset => {
                    const t = toEpochMs(asset.createdAt);
                    if (t === null) return;
                    const key = asset.recordingId || '';
                    if (!byRecording.has(key)) byRecording.set(key, []);
                    byRecording.get(key).push(t);
                });
            const recordings = [...byRecording.entries()]
                .map(([recordingId, times]) => ({ recordingId: recordingId || null, times: times.sort((x, y) => x - y) }))
                .sort((x, y) => x.times[0] - y.times[0]);
            return recordings.map(({ recordingId, times }, rank) => {
                const known = recordingId ? recordingTimes.get(recordingId) : null;
                const startedAt = toEpochMs(known?.startedAt);
                const stoppedAt = toEpochMs(known?.stoppedAt);
                return {
                id: `${userId}-${type}${recordingId ? `-${recordingId}` : ''}`,
                type,
                recordingId,
                label: `${MEDIA_LABELS[type]}${recordings.length > 1 ? ` ${rank + 1}` : ''} — ${displayName(entry.user, entry.role)}`,
                playlistUrl: `${mediaRoot}/hls/${userId}/${type}${recordingId ? `/r/${recordingId}` : ''}/playlist.m3u8`,
                // Début/fin du registre ; sinon heure de réception des fichiers (repli). Dans le flux,
                // les tags ID3 (TS:) donnent ensuite l'heure exacte de chaque image.
                startMs: startedAt ?? times[0],
                endMs: stoppedAt ?? times[times.length - 1],
                exactRange: stoppedAt !== null,
                segmentCount: times.length,
                };
            });
        });

        // Photo de la personne à l'arrivée (photo) et photos pendant le rendez-vous (gallery).
        const photos = userAssets
            .filter(asset => asset.mediaType === 'photo' || asset.mediaType === 'gallery')
            .map(asset => ({
                id: asset.id,
                t: toEpochMs(asset.createdAt),
                purpose: asset.mediaType === 'photo' ? 'arrival' : 'during',
                fileName: asset.fileName || 'photo.jpg',
                mimeType: asset.mimeType || 'image/jpeg',
                url: `${mediaRoot}/media/${asset.id}`,
            }))
            .filter(photo => photo.t !== null)
            .sort((x, y) => x.t - y.t);

        return {
            id: String(userId),
            userId,
            label: displayName(entry.user, entry.role),
            email: entry.user?.email || '',
            role: entry.role,
            color: TRACK_COLORS[index % TRACK_COLORS.length],
            positions: normalizePositions(locations.filter(location => sameUser(location.userId))),
            media,
            photos,
        };
    });

    const trackIdOf = userId => {
        const track = tracks.find(candidate => String(candidate.userId) === String(userId));
        return track ? track.id : null;
    };

    const messages = (Array.isArray(rdv.messages) ? rdv.messages : [])
        .map(message => ({
            id: String(message.id),
            t: toEpochMs(message.createdAt),
            trackId: trackIdOf(message.senderUserId),
            senderName: message.senderName || 'Utilisateur',
            text: messageText(message.message),
        }))
        .filter(message => message.t !== null)
        .sort((a, b) => a.t - b.t);

    const events = [];
    const pushEvent = (value, kind, label, trackId = null) => {
        const t = toEpochMs(value);
        if (t !== null) events.push({ t, kind, label, trackId });
    };
    pushEvent(rdv.createdAt, 'created', 'Rendez-vous créé');
    pushEvent(rdv.scheduledAt, 'scheduled', 'Heure prévue');
    tracks.forEach(track => {
        const isCreator = track.role === 'Organisateur';
        pushEvent(isCreator ? rdv.creatorJoinedAt : rdv.participantJoinedAt, 'joined', `${track.label} a rejoint`, track.id);
        pushEvent(isCreator ? rdv.creatorFoundAt : rdv.participantFoundAt, 'found', `${track.label} confirme la rencontre`, track.id);
        track.photos.forEach(photo => events.push({ t: photo.t, kind: 'photo', label: `${photo.purpose === 'arrival' ? 'Photo d’arrivée' : 'Photo'} de ${track.label}`, trackId: track.id, photoId: photo.id }));
    });
    pushEvent(rdv.foundAt, 'met', 'Rencontre confirmée');
    pushEvent(rdv.wellbeingConfirmedAt, 'wellbeing', 'Bien-être confirmé');
    pushEvent(rdv.completedAt, 'completed', 'Rendez-vous terminé');
    (Array.isArray(rdv.mediaConsents) ? rdv.mediaConsents : []).forEach(consent => {
        const label = MEDIA_LABELS[consent.mediaType] || consent.mediaType;
        const verdict = consent.status === 'accepted' ? 'acceptée' : consent.status === 'refused' ? 'refusée' : 'en attente';
        pushEvent(consent.decidedAt, 'consent', `Capture ${label.toLowerCase()} ${verdict}`, trackIdOf(consent.decidedBy));
    });
    events.sort((a, b) => a.t - b.t);

    const bundle = {
        id: rdv.id,
        kind: 'RENDEZ_VOUS',
        title: rdv.title || 'Rendez-vous sécurisé',
        status: rdv.status,
        address: rdv.address || null,
        flaggedInvestigation: Boolean(rdv.flaggedInvestigation),
        zone: {
            polygon: normalizeZonePolygon(rdv.zonePolygon),
            center: Number.isFinite(Number(rdv.latitude)) && Number.isFinite(Number(rdv.longitude))
                && rdv.latitude !== null && rdv.longitude !== null
                ? { lat: Number(rdv.latitude), lng: Number(rdv.longitude) } : null,
            radiusMeters: Number(rdv.radiusMeters) || null,
        },
        tracks,
        messages,
        events,
    };
    const range = bundleTimeRange(bundle);
    // La vue démarre au premier fait réel (arrivée, position, média) et pas à la
    // création du RDV, souvent des jours avant : sinon la timeline est vide.
    const activityTimes = [
        ...tracks.flatMap(track => [track.positions[0]?.t, ...track.media.map(m => m.startMs), ...track.photos.map(p => p.t)]),
        ...messages.map(m => m.t),
        ...events.filter(e => e.kind !== 'created' && e.kind !== 'scheduled').map(e => e.t),
    ].filter(Number.isFinite);
    // La relecture commence quand une personne signale qu'elle part au rendez-vous ;
    // les messages et le journal d'avant restent consultables.
    const departures = events.filter(e => e.kind === 'joined').map(e => e.t).filter(Number.isFinite);
    bundle.startMs = departures.length ? Math.min(...departures)
        : (activityTimes.length ? Math.min(...activityTimes) : range.startMs);
    // Et finit au dernier fait réel (fin d'enregistrement, message, position…), pas à l'heure
    // prévue ni à l'échéance du rendez-vous : sinon la timeline s'étire en gris.
    const lastTimes = [
        ...tracks.flatMap(track => [track.positions[track.positions.length - 1]?.t, ...track.media.map(m => m.endMs), ...track.photos.map(p => p.t)]),
        ...messages.map(m => m.t),
        ...events.filter(e => e.kind !== 'created' && e.kind !== 'scheduled').map(e => e.t),
    ].filter(Number.isFinite);
    bundle.endMs = lastTimes.length ? Math.max(...lastTimes) : range.endMs;
    if (bundle.startMs !== null && bundle.endMs !== null && bundle.endMs < bundle.startMs) bundle.endMs = bundle.startMs;
    return bundle;
}
